import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Drawer, Form, Select, Radio, message, Tabs, Tree, Space, Input, Button, Checkbox } from 'antd';
import { FileOutlined, FolderOutlined } from '@ant-design/icons';
import { useMeasure, useWindowSize } from 'react-use';
import { arrayToTree, traverseTree } from '@/utils/tree';
import styles from './index.module.less';
import { getDictTableData } from '@/pages/documentCompile/dict/utils';
import { DictData, DictTemplateCodeEnum } from '@/pages/documentCompile/dict/type';
import { getDataPackageTemplateList, getViewList, getViewDetail, getDataPackageDetail } from '@/pages/documentCompile/api/templatePackage';
import { useSetState } from 'ahooks';
import { getUUid, isEmpty } from '@/utils/tools'
import ProjectForm, { AddProjectAntdFormViewId } from './components/projectForm';
import { ReactComponent as ZlbzFolderIcon } from '@/assets/svg/zlbz-folder.svg'
import { ReactComponent as ZlbzArticleIcon } from '@/assets/svg/zlbz-article.svg'

interface Props {
    open: boolean;
    onCancel: () => void;
    onOk: (values: any) => void;
    record?: any | null;
    isEdit?: boolean;
}

// 状态管理接口
interface ModalState {
    // 数据列表
    projectTypeList: any[];
    templateList: any[];
    viewList: any[];
    treeData: any[];
    expandedKeys: string[];
    // 当前选中项
    currentProjectType: string;
    currentTemplateId: string;
    currentViewId: string;
    // 选中的节点数据
    checkNodes: { id: string; documentId: string; viewId: string }[];
    projectFormOpen: boolean;
    dictProjectData: DictData[];
    viewDetailData: any;
}

interface ViewSelectStatus {
    total: number;
    checkedCount: number;
    allChecked: boolean;
    indeterminate: boolean;
}

const EditModal: React.FC<Props> = ({
    open,
    onCancel,
    onOk,
    record,
    isEdit
}) => {
    const [form] = Form.useForm();
    const [treeRef, { top: treeTop }] = useMeasure();
    const { height: windowHeight } = useWindowSize();
    const refData = useRef<any>({
        dictProjectCodeMapData: {}
    });
    // 统一状态管理
    const [state, setState] = useSetState<ModalState>({
        projectTypeList: [],
        templateList: [],
        viewList: [],
        treeData: [],
        expandedKeys: [],
        currentProjectType: '',
        currentTemplateId: '',
        currentViewId: '',
        checkNodes: [],
        projectFormOpen: false,
        dictProjectData: [],
        viewDetailData: {},
    });

    // 计算当前视图的选中数据
    // 默认选中逻辑，经莫老师、志强大佬确认：
    // 1. 资料模板：如果存在默认选中默认，没有默认取第一条；用户选择之后，类型tabs切换时存在默认选中默认，没有默认取第一条。
    // 2. 视图：如果设置了默认就选中，没有默认就不选中；切换视图时，用户操作数据保持；选资料模板、视图时，清空用户选择数据
    const currentViewCheckedKeys = useMemo(() => {
        if (!state.treeData.length) {
            return [];
        }
        // 如果当前视图有勾选数据，直接返回当前视图的勾选数据
        if (state.checkNodes.length && state.checkNodes.find(node => node.viewId === state.currentViewId)) {
            // 如果有勾选数据，直接返回视图的勾选数据
            return state.checkNodes
                .filter(node => node.viewId === state.currentViewId)
                .map(node => node.id);
        }
        // 如果当前视图没有勾选数据，取消了勾选（全部取消了勾选），没有勾选（只勾选默认，默认可能为空）
        // 如果没有勾选数据，返回当前视图下的所有默认的文件节点
        // const selectedKeys = []
        // traverseTree(state.treeData, (node) => {
        //     if (node.type === 'file' && node.isDefault) {
        //         selectedKeys.push(node.id);
        //     }
        // });
        return [];
    }, [state.checkNodes, state.currentViewId, state.treeData]);
    // 经莫老师确认，提交所有选中的文档
    const currentViewSelectedDocuments = useMemo(() => {
        return state.checkNodes.reduce((acc, cur) => {
            if (!acc[cur.viewId]) {
                acc[cur.viewId] = []
            }
            acc[cur.viewId].push(cur.documentId)
            return acc
        }, {});
    }, [state.checkNodes]);

    const viewFileMap = useMemo(() => {
        if (!state.viewDetailData) {
            return {};
        }
        const map: Record<string, { key: string; documentId: string }[]> = {};
        Object.keys(state.viewDetailData).forEach((viewId) => {
            const files: { key: string; documentId: string }[] = [];
            const nodes = state.viewDetailData?.[viewId]?.treeData || [];
            traverseTree(nodes, (node) => {
                if (node.type === 'file') {
                    files.push({
                        key: String(node.key),
                        documentId: node.documentId
                    });
                }
            });
            map[viewId] = files;
        });
        return map;
    }, [state.viewDetailData]);

    const viewSelectStatus = useMemo(() => {
        const status: Record<string, ViewSelectStatus> = {};
        Object.keys(viewFileMap).forEach(viewId => {
            const total = viewFileMap[viewId]?.length || 0;
            const checkedCount = state.checkNodes.filter(item => item.viewId === viewId).length;
            status[viewId] = {
                total,
                checkedCount,
                allChecked: total > 0 && checkedCount === total,
                indeterminate: checkedCount > 0 && checkedCount < total
            };
        });
        return status;
    }, [state.checkNodes, viewFileMap]);
    // 计算当前视图的选中数据
    // const currentViewCheckedKeys = useMemo(() => {
    //     return state.checkNodes
    //         .filter(node => node.viewId === state.currentViewId)
    //         .map(node => node.id);
    // }, [state.checkNodes, state.currentViewId]);

    // const currentViewSelectedDocuments = useMemo(() => {
    //     return state.checkNodes
    //         .filter(node => node.viewId === state.currentViewId)
    //         .map(node => node.documentId);
    // }, [state.checkNodes, state.currentViewId]);

    // 初始化工程类型数据
    const initProjectTypes = async () => {
        try {
            const promiseList = [
                getDictTableData(DictTemplateCodeEnum.ENG_CLASS),
                getDictTableData(DictTemplateCodeEnum.PROJECT_ATTR)
            ]
            const [engClassList, dictProjectData] = await Promise.all(promiseList);
            engClassList.sort((a, b) => a.sort - b.sort)
            dictProjectData.push({ id: getUUid(), dictCode: 'zlgcmc', dictName: '资料包名称', attr_zdlx: 'dwb', sort: 0 })
            dictProjectData.sort((a, b) => a.sort - b.sort)
            const enableEngClassList = engClassList.filter(item => item.enable);
            setState({ projectTypeList: enableEngClassList, dictProjectData });
            for (const item of dictProjectData) {
                refData.current.dictProjectCodeMapData[item.dictCode] = item;
            }

            // 如果有数据,默认选中第一个
            if (enableEngClassList?.length > 0) {
                const firstType = enableEngClassList[0];
                setState({ currentProjectType: firstType.dictCode });
                // form.setFieldsValue({ projectType: firstType.dictCode });
                // 获取该工程类型下的资料模板
                fetchTemplateList(firstType.dictCode);
            }
        } catch (error) {
            message.error('获取工程类型失败');
        }
    };

    // 获取资料模板列表
    const fetchTemplateList = async (projectType: string) => {
        try {
            const templatesRes = await getDataPackageTemplateList({ projectType, pageIndex: 1, pageSize: 1000 });
            // const templates = templatesRes.result || [];
            const templates = (templatesRes.result || []).filter(item => item.isEnabled);
            setState({
                templateList: templates.map(item => ({
                    ...item,
                    label: item.templateName,
                    value: item.id
                })) || [],
                treeData: [], // 清空树数据
                expandedKeys: [], // 清空展开键
                checkNodes: [] // 清空勾选数据
            });

            // // 如果有数据,默认选中第一个
            // if (templates?.length > 0) {
            //     const firstTemplate = templates[0];
            //     setState({ currentTemplateId: firstTemplate.id });
            //     form.setFieldsValue({ templateId: firstTemplate.id });
            //     // 获取该模板下的视图列表
            //     fetchViewList(firstTemplate.id);
            // } else {
            //     setState({
            //         templateList: [],
            //         viewList: [],
            //         treeData: [],
            //         expandedKeys: []
            //     });

            //     setState({ currentTemplateId: '' });
            //     form.setFieldsValue({ templateId: null });
            // }
            // 莫老师确认，如果存在默认优先第一个默认否则默认选中第一个
            const defaultTemplate = templates?.reduce((acc, cur) => {
                if (!acc?.id || (!acc.isDefault && cur.isDefault)) {
                    acc = cur;
                }
                return acc
            }, {});
            setState({ currentTemplateId: defaultTemplate?.id || '' });
            form.setFieldsValue({ templateId: defaultTemplate?.id || null });
            if (defaultTemplate?.id) {
                // 获取该模板下的视图列表
                fetchViewList(defaultTemplate.id);
            }
        } catch (error) {
            message.error('获取资料模板失败');
        }
    };

    // 获取视图列表
    const fetchViewList = async (templateId: string) => {
        try {
            const views = await getViewList(templateId);
            setState({ viewList: views || [] });

            // 如果有数据,默认选中第一个
            if (views?.length > 0) {
                const firstView = views[0];
                setState({ currentViewId: firstView.id });
                // 获取该视图下的文档列表
                // fetchViewDetail(firstView.id);

                await fetchDataPackageDetail(templateId);
            } else {
                setState({
                    viewList: [],
                    treeData: [],
                    expandedKeys: [],
                    checkNodes: []
                });
            }
        } catch (error) {
            message.error('获取视图列表失败');
        }
    };

    // 获取视图详情(包含文档列表)
    const fetchViewDetail = async (viewId: string) => {
        try {
            // 先清空树数据，避免新旧数据混合导致 key 错误
            setState({
                treeData: [],
                expandedKeys: []
            });

            const detail = await getViewDetail(viewId);
            const { folders = [], files = [] } = detail;

            // 组合文件夹和文件数据
            const allItems = [
                ...folders.map(folder => ({
                    id: folder.id,
                    parentId: folder.parentId || '0',
                    name: folder.folderName,
                    type: 'folder',
                    icon: <FolderOutlined />,
                    isLeaf: false,
                    ...folder
                })),
                ...files.map(file => ({
                    id: file.id,
                    parentId: file.folderId || '0',
                    name: file.documentName,
                    type: 'file',
                    icon: <FileOutlined />,
                    isLeaf: true,
                    documentId: file.documentId,
                    ...file
                }))
            ];

            // 转换为树形结构
            const { tree: treeData, expandKey } = arrayToTree(allItems, {
                idField: 'id',
                pidField: 'parentId',
                keyField: 'id',
                titleField: 'name',
                needChildField: true
            });

            // 使用 traverseTree 遍历所有节点，设置禁用状态
            // traverseTree(treeData, (node) => {
            //     node.disabled = node.type === 'folder';
            //     node.disableCheckbox = node.type === 'folder';
            // });
            setState({
                treeData,
                expandedKeys: (expandKey || []).map(key => String(key))
            });
        } catch (error) {
            message.error('获取视图详情失败');
        }
    };

    // 获取资料详情(包含所有视图的文档、目录列表)
    const fetchDataPackageDetail = async (templateId: string) => {
        try {
            // 先清空树数据，避免新旧数据混合导致 key 错误
            setState({
                treeData: [],
                expandedKeys: []
            });
            const detail = await getDataPackageDetail(templateId);
            const { folderDTOS: folders = [], fileDTOS: files = [] } = detail;
            // 组合文件夹和文件数据
            const allItems = [
                ...folders.map(folder => ({
                    id: folder.id,
                    parentId: folder.parentId || '0',
                    name: folder.folderName,
                    type: 'folder',
                    icon: <ZlbzFolderIcon />,
                    isLeaf: false,
                    ...folder
                })),
                ...files.map(file => ({
                    id: file.id,
                    parentId: file.folderId || '0',
                    name: file.documentName,
                    type: 'file',
                    icon: <ZlbzArticleIcon />,
                    isLeaf: true,
                    documentId: file.documentId,
                    ...file
                }))
            ];

            // 根据viewId进行分组
            if (allItems.length) {
                const { groups, checkNodes } = allItems.reduce((acc, cur) => {
                    const viewId = cur.viewId || 'default';
                    if (!acc.groups[viewId]) {
                        acc.groups[viewId] = [];
                    }
                    acc.groups[viewId].push(cur);
                    if (cur.type === 'file' && cur.isDefault) {
                        acc.checkNodes.push(cur);
                    }
                    return acc;
                }, { groups: {}, checkNodes: [] });

                const viewDetailData = {}
                Object.keys(groups).forEach(viewId => {
                    const items = groups[viewId];
                    // 转换为树形结构
                    const { tree: treeData, expandKey } = arrayToTree(items, {
                        idField: 'id',
                        pidField: 'parentId',
                        keyField: 'id',
                        titleField: 'name',
                        needChildField: true
                    });

                    // 使用 traverseTree 遍历所有节点，设置禁用状态
                    // traverseTree(treeData, (node) => {
                    //     // node.disabled = node.type === 'folder';
                    //     node.disableCheckbox = node.type === 'folder';
                    // });
                    viewDetailData[viewId] = {
                        treeData,
                        expandKey: (expandKey || []).map(key => String(key))
                    };

                })
                setState({ checkNodes, viewDetailData })
            }
        } catch (error) {
            message.error('获取视图详情失败');
        }
    };

    useEffect(() => {
        if (!state.currentViewId || !state.viewDetailData) {
            setState({
                treeData: [],
                expandedKeys: [],
            });
        } else {
            setState({
                treeData: state.viewDetailData[state.currentViewId]?.treeData || [],
                expandedKeys: state.viewDetailData[state.currentViewId]?.expandKey || []
            });
        }
    }, [state.currentViewId, state.viewDetailData]);

    // 工程类型变更
    const handleProjectTypeChange = (projectType: any) => {
        setState({
            currentProjectType: projectType.dictCode,
            checkNodes: [] // 清空所有勾选数据
        });
        form.setFieldsValue({ projectType: projectType.dictCode });
        fetchTemplateList(projectType.dictCode);
    };

    // 资料模板变更
    const handleTemplateChange = (value: string) => {
        setState({
            currentTemplateId: value,
            checkNodes: [] // 清空所有勾选数据
        });
        fetchViewList(value);
    };

    // 视图Tab切换
    const handleViewChange = (viewId: string) => {
        setState({ currentViewId: viewId });
        // fetchViewDetail(viewId);
    };

    // 树形复选框变更
    const handleTreeCheck = (viewId: string, checkedKeys: any) => {
        const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked || [];

        // 先移除当前视图的所有选中项
        const newCheckNodes = state.checkNodes.filter(node => node.viewId !== viewId);

        // 目录也支持勾选，这里只提取文件节点的 documentId
        const findDocumentIds = (nodes: any[]) => {
            for (const node of nodes) {
                if (keys.includes(node.key) && node.type === 'file') {
                    newCheckNodes.push({
                        id: node.key,
                        documentId: node.documentId,
                        viewId: viewId
                    });
                }
                if (node.children) {
                    findDocumentIds(node.children);
                }
            }
        };

        findDocumentIds(state.treeData);

        setState({
            checkNodes: newCheckNodes
        });
    };

    const handleViewSelectAll = (viewId: string, checked: boolean) => {
        const viewFiles = viewFileMap[viewId] || [];
        const newCheckNodes = state.checkNodes.filter(node => node.viewId !== viewId);
        if (checked) {
            viewFiles.forEach(file => {
                newCheckNodes.push({
                    id: file.key,
                    documentId: file.documentId,
                    viewId
                });
            });
        }
        setState({ checkNodes: newCheckNodes });
    };

    // 提交表单 - 先弹出项目表单
    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            // 验证通过后，弹出项目表单
            setState({ projectFormOpen: true });
        } catch (error) {
            message.error('请检查表单填写是否正确');
        }
    };

    // 项目表单提交 - 获取项目信息后执行接口请求
    const handleFormOk = async (projectFormValues: any) => {
        try {
            const mainFormValues = await form.validateFields();

            // 过滤 AddProjectAntdFormViewId 
            // const projectAttrs = Object.keys(projectFormValues).filter(key => key !== AddProjectAntdFormViewId).map(key => {
            //     return {
            //         name: key,
            //         type: 'text',
            //         value: projectFormValues[key]
            //     }
            // });
            const projectAttrs = Object.keys(projectFormValues).map(key => {
                const item = refData.current.dictProjectCodeMapData[key];
                return {
                    name: item.dictCode,
                    type: item.attr_zdlx,
                    value: projectFormValues[key]
                }
            });
            // viewTmpls 所有选中的文档 [{viewTmplId: '视图id', selectedDocTmplIds: ['文档id']}]
            // const viewTmpls = [{
            //     viewTmplId: state.currentViewId,
            //     selectedDocTmplIds: currentViewSelectedDocuments
            // }];
            const viewTmpls = Object.keys(currentViewSelectedDocuments).map(viewId => ({
                viewTmplId: viewId,
                selectedDocTmplIds: currentViewSelectedDocuments[viewId]
            }));
            // 构建最终的提交参数
            const params = {
                projectName: projectFormValues.projectName || mainFormValues.projectName,
                // projectTypeCode: state.currentProjectType,
                dataPackageId: state.currentTemplateId,
                viewTmpls,
                projectAttrs
            };


            // 关闭项目表单
            setState({ projectFormOpen: false });

            // 执行接口请求
            onOk(
                // ...mainFormValues,
                // ...projectFormValues,
                // selectedDocuments: state.selectedDocuments,
                // projectType: state.currentProjectType,
                // templateId: state.currentTemplateId,
                // viewId: state.currentViewId,
                params
            );
        } catch (error) {
            message.error('提交失败，请重试');
        }
    }

    const handleJump = () => {
        setState({ projectFormOpen: false });
    };

    // 初始化数据
    useEffect(() => {
        if (!open) return;
        initProjectTypes();
    }, [open]);

    // 设置记录数据
    useEffect(() => {
        if (record) {
            form.setFieldsValue(record);
        }
    }, [record]);

    // console.log("treeTop", windowHeight - 300)

    return (
        <Drawer
            title="新建资料工程"
            open={open}
            className={styles.container}
            onClose={onCancel}
            destroyOnClose
            width="800px"
            placement="right"
            footer={
                <div className={styles.footer}>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={handleOk}>
                        确认
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical">
                {/* 资料工程名称 - 顶部 */}
                <div className={styles.projectHeader}>
                    <Form.Item
                        name="projectName"
                        label="资料包名称"
                        rules={[{ required: true, message: '请输入资料包名称' }]}
                    >
                        <Input
                            placeholder="请输入资料包名称"
                            defaultValue={record?.projectName}
                        />
                    </Form.Item>
                </div>

                <div className={styles.modalContent}>
                    {/* 工程类型和内容区域 */}
                    <Tabs
                        activeKey={state.currentProjectType}
                        onChange={(key) => {
                            const projectType = state.projectTypeList.find(item => item.dictCode === key);
                            if (projectType) {
                                handleProjectTypeChange(projectType);
                            }
                        }}
                        tabPosition="left"
                        className={styles.projectTypeTabs}
                        items={state.projectTypeList.map(item => ({
                            key: item.dictCode,
                            label: item.dictName,
                            // label1: (
                            //     <div className={styles.projectTypeTab}>
                            //         <div className={styles.typeIcon}>
                            //             {item.dictCode === 'ROAD' && '🛣️'}
                            //             {item.dictCode === 'CAMP' && '🏕️'}
                            //             {item.dictCode === 'FACTORY' && '🏭'}
                            //         </div>
                            //         <span className={styles.typeName}>{item.dictName}</span>
                            //     </div>
                            // ),
                            children: (
                                <div className={styles.rightPanel}>
                                    {/* 资料模板选择 */}
                                    <div className={styles.templateHeader}>
                                        <Form.Item
                                            name="templateId"
                                            label="资料模板"
                                            rules={[{ required: true, message: '请选择资料模板' }]}
                                        >
                                            <Select
                                                placeholder="请选择资料模板"
                                                onChange={handleTemplateChange}
                                                options={state.templateList}
                                            />
                                        </Form.Item>
                                    </div>

                                    {/* 视图Tabs */}
                                    {state.viewList.length > 0 && (
                                        <div className={styles.viewTabs}>
                                            <Tabs
                                                activeKey={state.currentViewId}
                                                onChange={handleViewChange}
                                                items={state.viewList.map(view => ({
                                                    key: view.id,
                                                    label: view.viewName,
                                                    children: (
                                                        <div className={styles.documentList} ref={treeRef}>
                                                            <div className={styles.documentListHeader}>
                                                                <Checkbox
                                                                    indeterminate={viewSelectStatus?.[view.id]?.indeterminate}
                                                                    checked={viewSelectStatus?.[view.id]?.allChecked}
                                                                    disabled={!viewFileMap?.[view.id]?.length}
                                                                    onChange={(e) => handleViewSelectAll(view.id, e.target.checked)}
                                                                >
                                                                    全选
                                                                </Checkbox>
                                                            </div>
                                                            <div className={styles.treeWrapper}>
                                                                <Tree
                                                                    checkable
                                                                    defaultExpandAll
                                                                    height={windowHeight - 400}
                                                                    treeData={state.treeData}
                                                                    expandedKeys={state.expandedKeys}
                                                                    checkedKeys={currentViewCheckedKeys}
                                                                    onCheck={(checkedKeys) => handleTreeCheck(view.id, checkedKeys)}
                                                                    onExpand={(expandedKeys) => setState({ expandedKeys: expandedKeys.map(String) })}
                                                                    titleRender={(nodeData) => (
                                                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                                                            {nodeData.icon}
                                                                            <span style={{ marginLeft: 8 }}>{nodeData.title}</span>
                                                                        </span>
                                                                    )}
                                                                    selectable={false}
                                                                    checkStrictly={false}
                                                                />
                                                            </div>
                                                        </div>
                                                    )
                                                }))}
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        }))}
                    />
                </div>
            </Form>

            <ProjectForm
                open={state.projectFormOpen}
                onCancel={() => setState({ projectFormOpen: false })}
                onOk={handleFormOk}
                // onJump={handleJump}
                dictData={state.dictProjectData}
                record={{ projectName: form.getFieldValue('projectName') }}
            />
        </Drawer>
    );
};

export default EditModal; 
